import { Box, Fab, useTheme, useMediaQuery, IconButton } from "@mui/material";
import { Chat as ChatIcon, Close as CloseIcon } from "@mui/icons-material";
import { useState, useEffect, useCallback } from "react";
import { useSuperQnaChat } from "./hooks/useSuperQnaChat";
import { UserInfoForm } from "./components/UserInfoForm";
import { ChatInterface } from "./components/ChatInterface";

/**
 * Floating AI Chat Props
 */
export interface FloatingAiChatProps {
  /**
   * 채팅창 열림 여부 (외부에서 제어 시)
   */
  isOpen?: boolean;

  /**
   * 채팅창 상태 변경 핸들러 (외부에서 제어 시)
   */
  onToggle?: (isOpen: boolean) => void;
}

/**
 * Floating AI Chat 컴포넌트
 * @description QNA 컨텐츠 관리를 위한 플로팅 AI 채팅 기능
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const FloatingAiChat = (props: FloatingAiChatProps) => {
  const { isOpen: externalIsOpen, onToggle } = props;

  // 내부 상태 (외부에서 제어하지 않을 때 사용)
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // 실제 사용할 상태 (외부 제어 우선)
  const isChatOpen =
    externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Super QnA Chat 훅 사용
  const {
    sessionUuid,
    userInfo,
    messages,
    isLoading,
    error,
    isSessionActive,
    startChat,
    sendMessage,
    resetChat,
    endChat,
    restoreSession,
    clearError,
  } = useSuperQnaChat();

  const toggleChat = useCallback(() => {
    const newState = !isChatOpen;

    if (onToggle) {
      onToggle(newState);
    } else {
      setInternalIsOpen(newState);
    }
  }, [isChatOpen, onToggle]);

  // 채팅창이 열릴 때 기존 세션 복원 시도
  useEffect(() => {
    if (isChatOpen && sessionUuid && !isSessionActive) {
      restoreSession();
    }
  }, [isChatOpen, sessionUuid, isSessionActive, restoreSession]);

  // ESC 키로 채팅창 닫기
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isChatOpen) {
        toggleChat();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isChatOpen, toggleChat]);

  // 모바일에서 채팅창이 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (isMobile && isChatOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobile, isChatOpen]);

  return (
    <>
      {/* 🔥 AI Chat floating button */}
      {!isChatOpen && (
        <Fab
          sx={{
            position: "fixed",
            bottom: { xs: 80, sm: 90 },
            right: { xs: 16, sm: 24 },
            backgroundColor: "#ff4081",
            color: "white",
            boxShadow: "0 8px 16px rgba(255, 64, 129, 0.3)",
            border: "2px solid white",
            "&:hover": {
              backgroundColor: "#e91e63",
              transform: "scale(1.1)",
              boxShadow: "0 12px 24px rgba(255, 64, 129, 0.4)",
            },
            zIndex: 9999,
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%": {
                boxShadow: "0 0 0 0 rgba(255, 64, 129, 0.7)",
              },
              "70%": {
                boxShadow: "0 0 0 10px rgba(255, 64, 129, 0)",
              },
              "100%": {
                boxShadow: "0 0 0 0 rgba(255, 64, 129, 0)",
              },
            },
          }}
          onClick={toggleChat}
          aria-label="AI Chat"
        >
          <ChatIcon />
        </Fab>
      )}

      {/* 🚀 플로팅 AI 채팅 패널 */}
      {isChatOpen && (
        <>
          {/* 배경 오버레이 (모바일에서만 표시) */}
          {isMobile && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1299,
                animation: "fadeIn 0.3s ease-out",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0 },
                  "100%": { opacity: 1 },
                },
              }}
              onClick={toggleChat}
            />
          )}

          {/* 플로팅 채팅 창 */}
          <Box
            sx={{
              position: "fixed",
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              width: { xs: "calc(100vw - 32px)", sm: 400, md: 450 },
              maxWidth: "90vw",
              height: { xs: "calc(100vh - 120px)", sm: "70vh", md: "75vh" },
              maxHeight: "calc(100vh - 100px)",
              zIndex: 1300,
              borderRadius: 3,
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
              animation: "slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "@keyframes slideInUp": {
                "0%": {
                  transform: "translateY(100%) scale(0.9)",
                  opacity: 0,
                },
                "100%": {
                  transform: "translateY(0) scale(1)",
                  opacity: 1,
                },
              },
            }}
          >
            {/* 채팅 창 내부 컨테이너 */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: 3,
                overflow: "hidden",
                backgroundColor: "background.paper",
              }}
            >
              {/* 채팅 컨텐츠 */}
              {!isSessionActive ? (
                // 사용자 정보 입력 폼
                <UserInfoForm
                  onSubmit={startChat}
                  isLoading={isLoading}
                  error={error}
                  onClose={toggleChat}
                />
              ) : (
                // 채팅 인터페이스
                <ChatInterface
                  messages={messages}
                  userInfo={userInfo}
                  isLoading={isLoading}
                  error={error}
                  onSendMessage={sendMessage}
                  onResetChat={resetChat}
                  onEndChat={() => {
                    endChat();
                    toggleChat();
                  }}
                  onClearError={clearError}
                  onMinimize={toggleChat}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </>
  );
};
